/**
 * Dessine chaque page d'un PDF dans `host`, un `<canvas>` par page, à la largeur du cadre.
 *
 * ⚠️ **pdf.js se charge à la demande** : ~1,6 Mo avec son worker, que seuls les écrans qui
 * montrent un document téléchargent. Il ne pèse pas sur le premier chargement de l'app.
 *
 * La définition suit l'écran (`devicePixelRatio`) et le zoom ; la largeur, elle, est tenue en
 * pourcentage du cadre, si bien qu'un cadre qui rétrécit après coup garde la page entière.
 * `zoom` à 1 : la page à la largeur du cadre ; au-delà, le cadre défile dans les deux sens.
 */
export async function renderPdfPages(
  src: string,
  host: HTMLElement,
  signal: AbortSignal,
  zoom = 1,
): Promise<void> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default
  // Abandonné pendant le premier chargement de pdf.js : rien n'est encore ouvert, rien ne doit l'être.
  if (signal.aborted) return

  const loading = pdfjs.getDocument({ url: src })
  signal.addEventListener('abort', () => void loading.destroy(), { once: true })
  const document = await loading.promise

  // Un cadre pas encore mis en page mesure 0 : la page se dessine alors à sa taille d'origine.
  const width = host.clientWidth
  const pages = []
  for (let number = 1; number <= document.numPages; number += 1) {
    if (signal.aborted) return
    const page = await document.getPage(number)
    const natural = page.getViewport({ scale: 1 })
    const scale = (width > 0 ? (width * zoom) / natural.width : zoom) * window.devicePixelRatio
    const viewport = page.getViewport({ scale })
    const canvas = host.ownerDocument.createElement('canvas')
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    canvas.style.inlineSize = `${String(zoom * 100)}%`
    pages.push({ page, viewport, canvas })
  }
  // pdf.js ne rejette pas une page demandée avant sa destruction : un rendu abandonné s'arrête ici.
  if (signal.aborted) return

  // Le point lu reste sous les yeux d'un cran de zoom à l'autre (#585) : vider le cadre puis le
  // remplir page à page le ramenait en haut de la première.
  const frame = host.parentElement
  const top = frame === null || frame.scrollHeight === 0 ? 0 : frame.scrollTop / frame.scrollHeight
  const left = frame === null || frame.scrollWidth === 0 ? 0 : frame.scrollLeft / frame.scrollWidth
  host.replaceChildren(...pages.map(({ canvas }) => canvas))
  frame?.scrollTo(left * frame.scrollWidth, top * frame.scrollHeight)

  for (const { page, viewport, canvas } of pages) {
    if (signal.aborted) return
    await page.render({ canvas, viewport }).promise
  }
}
